// Field specs for the client-record settings sections. Kept out of the screen
// so the tab bodies stay readable; each drives a <SettingsSection /> form.

export const BILLING_FIELDS = [
  { key: 'billing_address', label: 'Billing address', full: true, group: 'Invoicing',
    placeholder: 'If different from the main address' },
  { key: 'invoice_email', label: 'Invoice email', type: 'email', group: 'Invoicing' },
  { key: 'payment_terms_days', label: 'Payment terms (days)', type: 'number', integer: true, group: 'Invoicing',
    hint: 'Used for cash-flow forecasting' },
  { key: 'invoice_delivery', label: 'Invoice delivery', type: 'select', group: 'Invoicing',
    options: [['attachment', 'PDF attachment'], ['embedded', 'Embedded in email body']] },

  { key: 'po_required', label: 'PO number required', type: 'select', group: 'Purchase orders',
    options: [['optional', 'Optional'], ['yes', 'Required on every invoice'], ['no', 'Never used']] },
  { key: 'po_format', label: 'PO format', group: 'Purchase orders',
    placeholder: 'e.g. PO-XXXXXX', hint: 'Plain-language description of the format' },
  { key: 'po_format_regex', label: 'PO validation pattern', group: 'Purchase orders',
    placeholder: 'e.g. ^PO-\\d{6}$', hint: 'Optional regex to validate the entered number' },

  // Opposite rules between clients — some AP systems reject invoices WITH extra
  // attachments, others reject them WITHOUT. Both are explicit toggles.
  { key: 'attach_po', label: 'Attach the PO to the invoice', type: 'bool', group: 'Attachment rules' },
  { key: 'attach_worksheet', label: 'Attach the completed worksheet', type: 'bool', group: 'Attachment rules' },
  { key: 'attach_certificates', label: 'Attach test certificates', type: 'bool', group: 'Attachment rules',
    hint: 'Some AP systems reject invoices carrying anything beyond the invoice PDF' },

  { key: 'payment_method', label: 'Payment method', type: 'select', group: 'Payment',
    options: [['', '—'], ['bacs', 'BACS'], ['card', 'Card'], ['cheque', 'Cheque'], ['other', 'Other']] },
  { key: 'bank_reference', label: 'Required payment reference', group: 'Payment' },
  { key: 'vat_status', label: 'VAT status', group: 'Payment', placeholder: 'e.g. Standard rated' },
  { key: 'cis_applies', label: 'Subject to CIS', type: 'bool', group: 'Payment',
    hint: 'Construction Industry Scheme changes how the invoice is constructed' },
  { key: 'cis_number', label: 'CIS verification number', group: 'Payment' },

  { key: 'quote_auth_threshold', label: 'Quote sign-off threshold (£)', type: 'number', group: 'Authorisation',
    hint: 'Quotes above this need internal sign-off before being sent' },
  { key: 'quote_auth_notes', label: 'Who signs off', group: 'Authorisation', full: true,
    placeholder: 'e.g. Any quote to this client needs Joe and Harvey' },

  { key: 'notes', label: 'Billing notes', type: 'textarea', full: true, group: 'Notes',
    placeholder: 'Anything about this client’s invoicing that the fields above don’t cover' },
];

export const ACCESS_FIELDS = [
  { key: 'permit_system', label: 'Permit-to-work system', group: 'Permits',
    placeholder: 'e.g. Sirius, Zutec, in-house form' },
  { key: 'permit_lead_days', label: 'Permit lead time (days)', type: 'number', group: 'Permits',
    hint: 'How far ahead a permit must be raised' },
  { key: 'permit_notes', label: 'Permit process notes', type: 'textarea', full: true, group: 'Permits' },

  { key: 'default_managed_site', label: 'Sites are managed by default', type: 'bool', group: 'Site access',
    hint: 'Drives whether a permit is assumed necessary. Individual properties can differ.' },
  { key: 'key_holder', label: 'Key holder / access contact', group: 'Site access' },
  { key: 'site_hours', label: 'Site access hours', group: 'Site access', placeholder: 'e.g. Mon–Fri 07:00–18:00' },
  { key: 'parking_notes', label: 'Parking', group: 'Site access', full: true },

  { key: 'induction_required', label: 'Security induction required', type: 'bool', group: 'Induction' },
  { key: 'induction_url', label: 'Induction link', type: 'url', group: 'Induction', placeholder: 'https://' },
  { key: 'induction_renewal_months', label: 'Re-induction every (months)', type: 'number', group: 'Induction' },

  { key: 'ppe_requirements', label: 'PPE requirements', type: 'textarea', full: true, group: 'On site',
    placeholder: 'Anything beyond standard site PPE' },
  { key: 'pre_site_checks', label: 'Pre-site checks', type: 'textarea', full: true, group: 'On site',
    placeholder: 'Checks that must be completed and signed off before arriving' },
];

export const PORTAL_FIELDS = [
  { key: 'brand_colour', label: 'Brand colour', type: 'colour', group: 'Branding', placeholder: '#1A80E5', hint: 'A hex colour, e.g. #1A80E5' },
  { key: 'logo_path', label: 'Logo URL', type: 'url', group: 'Branding', placeholder: 'https://' },
  { key: 'welcome_text', label: 'Portal welcome text', type: 'textarea', full: true, group: 'Branding' },

  { key: 'share_level', label: 'Data sharing default', type: 'select', group: 'Permissions',
    options: [['owned', 'Only assets they own or maintain'], ['all', 'Everything on their properties'], ['minimal', 'Compliance status only']] },
  { key: 'can_download_reports', label: 'Can download reports', type: 'bool', group: 'Permissions' },
  { key: 'can_raise_queries', label: 'Can raise queries', type: 'bool', group: 'Permissions' },
  { key: 'can_upload_documents', label: 'Can upload documents', type: 'bool', group: 'Permissions' },
];

export const OBLIGATION_TYPES = [
  ['insurance', 'Insurance'], ['ssip', 'SSIP membership'], ['iso', 'ISO certification'],
  ['dbs', 'DBS'], ['right_to_work', 'Right to work'], ['reference', 'References'], ['other', 'Other'],
];

export const DOC_TYPES = [
  ['rams', 'RAMS template'], ['permit', 'Permit-to-work form'], ['signin', 'Site sign-in sheet'],
  ['spec', 'Specification'], ['contract', 'Contract / framework'], ['other', 'Other'],
];

export const NOTE_TYPES = [
  ['note', 'Note'], ['contract', 'Contract signed'], ['scope_change', 'Scope change'],
  ['escalation', 'Escalation'], ['complaint', 'Complaint'], ['remediation', 'Remediation'],
];

/* ── Shared by the Clients list and the client record ────────────────────── */

export const CLIENT_TYPES = [
  ['managing_agent', 'Managing agent'], ['building_owner', 'Building owner'],
  ['facilities_management', 'Facilities management company'],
  ['housing_association', 'Housing association'], ['corporate', 'Corporate'],
  ['local_authority', 'Local authority'], ['private_landlord', 'Private landlord'], ['other', 'Other'],
];
export const clientTypeLabel = (t) => (CLIENT_TYPES.find(([k]) => k === t) || [null, null])[1];

/** Client status → chip tone + label. Status is always shown in words. */
export const CLIENT_STATUSES = [
  ['active', 'Active', 'success'],
  ['prospect', 'Prospect', 'info'],
  ['dormant', 'Dormant', 'warning'],
  ['archived', 'Archived', 'muted'],
];
export const clientStatusOf = (s) => {
  const hit = CLIENT_STATUSES.find(([k]) => k === (s || 'active')) || CLIENT_STATUSES[0];
  return { key: hit[0], label: hit[1], tone: hit[2] };
};

// Inline validation for contact details. Each returns an error sentence or null;
// blank values are allowed (the fields are optional).
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
export const checkEmail = (v) => (!String(v || '').trim() || EMAIL_RE.test(String(v).trim()) ? null : 'Enter an email address like name@company.co.uk');
/** UK numbers: 07…, 01…/02…/03…, 08…, or +44 — 10 or 11 digits once spaces and brackets are removed. */
export const checkUkPhone = (v) => {
  const s = String(v || '').trim();
  if (!s) return null;
  const digits = s.replace(/[\s()-]/g, '');
  if (/^\+44\d{9,10}$/.test(digits) || /^0\d{9,10}$/.test(digits)) return null;
  return 'Enter a UK phone number, e.g. 020 7946 0102 or 07700 900123';
};
export const checkWebsite = (v) => {
  const s = String(v || '').trim();
  if (!s) return null;
  try {
    const u = new URL(/^[a-z][a-z0-9+.-]*:/i.test(s) ? s : `https://${s}`);
    if (!/^https?:$/.test(u.protocol) || !u.hostname.includes('.')) throw new Error('bad');
    return null;
  } catch { return 'Enter a web address, e.g. www.company.co.uk'; }
};
export const checkPostcode = (v) => {
  const s = String(v || '').trim();
  if (!s) return null;
  return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(s) ? null : 'Enter a UK postcode, e.g. EC4V 3AB';
};

/** Validate the client profile form → { field: message }. */
export const validateClientForm = (f) => {
  const e = {};
  if (!String(f.name || '').trim()) e.name = 'Enter the client’s name';
  const email = checkEmail(f.contact_email); if (email) e.contact_email = email;
  const phone = checkUkPhone(f.contact_phone); if (phone) e.contact_phone = phone;
  const web = checkWebsite(f.website); if (web) e.website = web;
  const pc = checkPostcode(f.postcode); if (pc) e.postcode = pc;
  return e;
};
